import type { ThreeEvent } from '@react-three/fiber';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import type { TileVisibility } from '@/game/sim';
import type { HexMapProperties, HexTileCoordinates, HexTileMapKey } from '../model/types';
import TilePicker from './TilePicker';
import { createTileBoard } from './tileBoard';

interface HexMapViewProperties extends HexMapProperties {
  onTilePointerDown?: (coordinates: HexTileCoordinates, event: ThreeEvent<PointerEvent>) => void;
  /**
   * Fog-of-war lookup for a tile. Its identity changes only when the visible
   * set changes (`Fog.sight` is rebuilt per fog version), which is what
   * re-reads the tiles.
   */
  getTileVisibility?: (key: HexTileMapKey) => TileVisibility;
}

const allVisible = (): TileVisibility => 'visible';

/**
 * The board: every tile drawn by three InstancedMeshes (`tileBoard.ts`), only
 * the tiles whose visibility changed rewritten on a vision bump, and pointer
 * events taken by one ground plane (`TilePicker`) instead of the tiles.
 */
const HexMap = memo(function HexMap({
  tiles,
  onTilePointerDown,
  getTileVisibility = allVisible,
}: HexMapViewProperties) {
  const board = useMemo(() => createTileBoard(tiles), [tiles]);
  useEffect(() => () => board.dispose(), [board]);
  useLayoutEffect(() => {
    board.update(getTileVisibility);
  }, [board, getTileVisibility]);

  const handlePointerDown = useCallback(
    (key: HexTileMapKey, event: ThreeEvent<PointerEvent>) => {
      const tile = tiles.get(key);
      if (tile) onTilePointerDown?.(tile.coordinates, event);
    },
    [tiles, onTilePointerDown]
  );

  return (
    <group>
      <primitive object={board.land} dispose={null} />
      <primitive object={board.water} dispose={null} />
      <primitive object={board.fog} dispose={null} />
      <TilePicker tiles={tiles} onPointerDown={onTilePointerDown && handlePointerDown} />
    </group>
  );
});

export default HexMap;
